import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { initFirebase } from '../firebase';

const BusinessContext = createContext(null);

export const BusinessProvider = ({ children }) => {
  const { db } = initFirebase();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  const fetchBusinesses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Query customers collection directly - business data is at customer level
      const customersRef = collection(db, 'customers');
      const customersQuery = query(customersRef, where('active', '==', true));
      const customersSnapshot = await getDocs(customersQuery);

      const allBusinesses = customersSnapshot.docs.map((customerDoc) => ({
        id: customerDoc.id,
        customerId: customerDoc.id,
        ...customerDoc.data(),
      }));

      setBusinesses(allBusinesses);
    } catch (err) {
      console.error('Error fetching businesses:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [db]);

  const getBusinessById = useCallback((businessId) => {
    return businesses.find((b) => b.id === businessId);
  }, [businesses]);

  useEffect(() => {
    fetchBusinesses();
  }, [fetchBusinesses]);

  const value = {
    businesses,
    loading,
    error,
    selectedBusiness,
    setSelectedBusiness,
    fetchBusinesses,
    getBusinessById,
  };

  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
};

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
};

export default BusinessContext;
